import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  checkIn,
  checkOut,
  recordAttendance,
  updateAttendance,
  fetchAttendanceByShift,
  fetchAttendance,
} from "../api/attendanceApi";
import type {
  RecordAttendanceRequest,
  UpdateAttendanceRequest,
  AttendanceParams,
} from "./types";

export const attendanceKeys = {
  all: ["attendance"] as const,
  list: (params: AttendanceParams) =>
    [...attendanceKeys.all, "list", params] as const,
  byShift: (shiftId: number) =>
    [...attendanceKeys.all, "shift", shiftId] as const,
};

export const useAttendance = (params: AttendanceParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: attendanceKeys.list(params),
    queryFn: () => fetchAttendance(params),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled,
  });
};

export const useAttendanceByShift = (shiftId: number) => {
  return useQuery({
    queryKey: attendanceKeys.byShift(shiftId),
    queryFn: () => fetchAttendanceByShift(shiftId),
    enabled: !!shiftId,
  });
};

export const useCheckIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: number) => checkIn(shiftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      toast.success("Checked in successfully");
    },
  });
};

export const useCheckOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: number) => checkOut(shiftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      toast.success("Checked out successfully");
    },
  });
};

export const useRecordAttendance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RecordAttendanceRequest) => recordAttendance(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      toast.success("Attendance recorded");
    },
  });
};

export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateAttendanceRequest;
    }) => updateAttendance(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      queryClient.invalidateQueries({ queryKey: ["payroll", "preflight"] });
      toast.success("Attendance updated");
    },
  });
};
